import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@connectrpc/connect-query";
import { ChevronLeftIcon, ChevronRightIcon, LayersIcon, SearchIcon } from "lucide-react";

import { ErrorState } from "@/components/error-state";
import { EmptyState, PageHeader } from "@/components/page-header";
import { StateCountBar, StateCountLegend } from "@/components/state-count-bar";
import { BatchStatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { BatchStatus } from "@/gen/bmi/v1/batch_message_pb";
import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { formatBoth, formatCount } from "@/lib/format";
import { usePager } from "@/lib/paging";

const ALL = "__all__";
const PAGE_SIZE = 25;

export function BatchesPage() {
  const [accountId, setAccountId] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [reference, setReference] = useState("");
  // Debounced so every keystroke is not a round trip; ListBatches filters
  // server-side now.
  const [referenceQuery, setReferenceQuery] = useState("");

  useEffect(() => {
    const handle = window.setTimeout(() => setReferenceQuery(reference.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [reference]);

  const pager = usePager(`${accountId}|${status}|${referenceQuery}`);

  const accountsQuery = useQuery(AdminService.method.listAccounts, {});
  const accounts = accountsQuery.data?.accounts ?? [];
  const accountLabels = useMemo(
    () => new Map(accounts.map((a) => [a.accountId, a.label])),
    [accounts],
  );

  const batchesQuery = useQuery(AdminService.method.listBatches, {
    accountId: accountId === ALL ? "" : accountId,
    status: status === ALL ? undefined : (Number(status) as BatchStatus),
    reference: referenceQuery,
    pageSize: PAGE_SIZE,
    pageToken: pager.token,
  });

  const batches = batchesQuery.data?.batches ?? [];
  const nextPageToken = batchesQuery.data?.nextPageToken ?? "";

  // AdminListBatchesResponse carries no per-batch account id, so the owner can
  // only be named when the list is already scoped to one account.
  const scopedOwnerLabel = accountId === ALL ? undefined : accountLabels.get(accountId);

  return (
    <>
      <PageHeader
        title="Batches"
        description="Every batch submitted through the BMI surface, with its per-state message counts."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Filter by reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>

        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All accounts</SelectItem>
            {accounts.map((a) => (
              <SelectItem key={a.accountId} value={a.accountId}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any status</SelectItem>
            <SelectItem value={String(BatchStatus.PENDING)}>Pending</SelectItem>
            <SelectItem value={String(BatchStatus.COMPLETED)}>Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {batchesQuery.isError ? (
        <ErrorState
          error={batchesQuery.error}
          title="Could not load batches"
          onRetry={() => void batchesQuery.refetch()}
        />
      ) : batchesQuery.isPending ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : batches.length === 0 ? (
        <EmptyState
          icon={<LayersIcon />}
          title="No batches match"
          description="Adjust the filters, or wait for a CLIENT to submit one."
        />
      ) : (
        <div className="space-y-3">
          {batches.map((batch) => {
            return (
              <Card key={batch.batchId} className="gap-3">
                <CardHeader className="gap-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <Link
                        to={`/batches/${batch.batchId}`}
                        className="truncate font-medium hover:underline"
                      >
                        {batch.reference ?? <span className="text-muted-foreground">(no reference)</span>}
                      </Link>
                      <p className="text-xs text-muted-foreground">
                        <code className="font-mono">{batch.batchId}</code>
                        {scopedOwnerLabel ? <> · {scopedOwnerLabel}</> : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <BatchStatusBadge status={batch.status} />
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/batches/${batch.batchId}`}>Open</Link>
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-2">
                  <StateCountBar counts={batch.stateCounts} total={batch.messageCount} />
                  <StateCountLegend counts={batch.stateCounts} />
                  <p className="text-xs text-muted-foreground">
                    {formatCount(batch.messageCount)} messages · submitted{" "}
                    {formatBoth(batch.createdAt)}
                    {batch.completedAt ? <> · completed {formatBoth(batch.completedAt)}</> : null}
                  </p>
                </CardContent>
              </Card>
            );
          })}

          <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Previous page"
              disabled={!pager.hasPrev}
              onClick={pager.goPrev}
            >
              <ChevronLeftIcon />
            </Button>
            <span className="tabular px-2 text-xs text-muted-foreground">
              page {pager.page + 1}
            </span>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Next page"
              disabled={nextPageToken === ""}
              onClick={() => pager.goNext(nextPageToken)}
            >
              <ChevronRightIcon />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
